import * as React from 'react';

import { App, Col, Row, Steps } from 'antd';
import { useAppDispatch, useAppSelector } from 'src/types/reduxHelpers';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { AboutCompany } from 'src/components/interviewComponents/aboutCompany';
import { AnimatedHeader } from 'src/components/animatedHeader';
import { AppButton } from 'src/components/appButton';
import { GeneralFeedback } from 'src/components/interviewComponents/generalFeedback';
import { InterviewResponseDraft } from 'exit-interview.shared/models/dataModels/interviewResponse';
import { InterviewStepHandle } from 'src/components/interviewComponents/types/interviewStepHandle';
import { JobDetails } from 'src/components/interviewComponents/jobDetails';
import { PageWrapper } from 'src/components/pageWrapper';
import { PersonalInfo } from 'src/components/interviewComponents/personalInfo';
import { ReasonForLeaving } from 'src/components/interviewComponents/reasonForLeaving';
import { ReviewSubmit } from 'src/components/interviewComponents/reviewSubmit';
import { WagesAndBenefits } from 'src/components/interviewComponents/wagesAndBenefits';
import { createInterview } from 'src/store/modules/interviewModule';
import { useApiErrorMessage } from 'src/hooks/useApiErrorMessage';
import { useAppRoutes } from 'src/hooks';
import { useTranslation } from 'react-i18next';

interface InterviewPageProps {

}

export const InterviewPage: React.FC<InterviewPageProps> = (props) => {
    const { t } = useTranslation();
    const { getErrorMessage } = useApiErrorMessage();

    const navigate = useNavigate();

    const dispatch = useAppDispatch();
    const appRoutes = useAppRoutes();

    const interviewStepRef = React.useRef<InterviewStepHandle>(null);

    const steps = [
        {
            key: 'personal info',
            title: t('Pages.InterviewPage.steps.personal_info_title'),
            content: <PersonalInfo ref={interviewStepRef} />,
        },
        {
            key: 'reason for leaving',
            title: t('Pages.InterviewPage.steps.reason_for_leaving_title'),
            content: <ReasonForLeaving ref={interviewStepRef} />,
        },
        {
            key: 'job details',
            title: t('Pages.InterviewPage.steps.job_details_title'),
            content: <JobDetails ref={interviewStepRef} />,
        },
        {
            key: 'wages and benefits',
            title: t('Pages.InterviewPage.steps.wages_and_benefits_title'),
            content: <WagesAndBenefits ref={interviewStepRef} />,
        },
        {
            key: 'the company',
            title: t('Pages.InterviewPage.steps.company_title'),
            content: <AboutCompany ref={interviewStepRef} />,
        },
        {
            key: 'general feedback',
            title: t('Pages.InterviewPage.steps.general_feedback_title'),
            content: <GeneralFeedback ref={interviewStepRef} />,
        },
        {
            key: 'review',
            title: t('Pages.InterviewPage.steps.review_title'),
            content: <ReviewSubmit />,
        },
    ];

    const [currentStepIndex, setCurrentStepIndex] = React.useState(0);

    const [searchParams, setSearchParams] = useSearchParams();
    const stepSearchParam = searchParams.get('step') ?? undefined;

    const employeeReferenceId = useAppSelector((state) => state.invitation.employee?.employeeReferenceId);
    const aboutCompany = useAppSelector((state) => state.interview.aboutCompany);
    const generalFeedback = useAppSelector((state) => state.interview.generalFeedback);
    const jobDetails = useAppSelector((state) => state.interview.jobDetails);
    const reasonsForLeaving = useAppSelector((state) => state.interview.reasonsForLeaving);
    const wagesAndBenefits = useAppSelector((state) => state.interview.wagesAndBenefits);

    const [isLoading, setIsLoading] = React.useState(false);

    const { notification } = App.useApp();

    const handleSubmit = async (): Promise<void> => {
        if (!employeeReferenceId || !aboutCompany || !generalFeedback || !jobDetails || !reasonsForLeaving || !wagesAndBenefits) {
            // TODO: This shouldn't happen, maybe add a message here just in case
            notification.error({
                message: t('Common.error'),
                description: 'How can you make this happen?',
            });
            return;
        }

        const interviewResponseDrafts: Array<InterviewResponseDraft> = [];

        const allResponses = {
            ...aboutCompany,
            ...generalFeedback,
            ...jobDetails,
            ...reasonsForLeaving,
            ...wagesAndBenefits,
        };

        Object.entries(allResponses).forEach((entry) => {
            const [key, value] = entry;

            interviewResponseDrafts.push({
                answer: Array.isArray(value)
                    ? value.join('|')
                    : value?.toString() ?? null,
                questionKey: key,
            });
        });

        setIsLoading(true);
        const action = await dispatch(createInterview({
            employeeReferenceId: employeeReferenceId,
            responses: interviewResponseDrafts,
        }));
        setIsLoading(false);

        if (createInterview.rejected.match(action)) {
            // Failed!
            notification.error({
                message: t('Common.error'),
                description: getErrorMessage(action.payload),
            });
        } else {
            // Passed!
            navigate(appRoutes.interviewCompleted.path);
        }
    };

    const handleNext = async (): Promise<void> => {
        if (currentStepIndex < steps.length - 1) {
            // Try to submit the current step
            const isStepSubmitted = await interviewStepRef.current?.submit() ?? true;

            if (isStepSubmitted) {
                // If we got 'true' from the step's submit handle (or if it didnt have one), assume
                // that everything's ok for this step and move on
                const nextStep = steps[currentStepIndex + 1];

                if (!!nextStep) {
                    // Set the 'step' search param to the key of the next step
                    // This change will be seen in our useEffect below to update the currentStepIndex value
                    setSearchParams({
                        step: nextStep.key,
                    });
                }
            }
        }
    };

    const handlePrevious = (): void => {
        if (currentStepIndex > 0) {
            const previousStep = steps[currentStepIndex - 1];

            if (!!previousStep) {
                // Set the 'step' search param to the key of the previous step
                // This change will be seen in our useEffect below to update the currentStepIndex value
                setSearchParams({
                    step: previousStep.key,
                });
            }
        }
    };

    React.useEffect(() => {
        // This effect will run on first mount, and whenever the value of the 'step' search param changes.
        // Since we want to control the content shown via the 'step' search param, this effect will update the currentStepIndex value with the step whose key matches the step's key to keep the content in sync with the url
        // If no step matches the key in the search param (or if we don't have a 'step' search param), we'll set the 'step' search param to the key of the first step to start at the beginning

        // Try to find the step that has a key with the same value of our 'step' search param
        const stepIndex = steps.findIndex((step) => step.key === stepSearchParam);

        if (stepIndex === -1) {
            // We couldn't find the index for the step we're on. Either it:
            // - Wasn't found (something went horribly wrong, or the user entered their own search param), or
            // - We didnt have a 'step' search param
            const firstStep = steps[0];

            if (!firstStep) {
                // This should never happen since we're declaring our steps in a static array above (and it obviously has at least one item).
                return;
            }

            // Set it to the first step to start from the beginning
            setSearchParams({
                step: firstStep.key,
            }, {
                replace: true,
            });
        } else {
            // We found the index for the step we have in the url
            // Set the currentStep to the new index!
            setCurrentStepIndex(stepIndex);
        }
    }, [stepSearchParam]);

    return (
        <PageWrapper
            isLoading={isLoading}
        >
            <Row
                align="middle"
                justify="space-around"
                gutter={[20, 30]}
            >
                <Col
                    span={24}
                    style={{ textAlign: 'center' }}
                >
                    <AnimatedHeader
                        header={t('Pages.InterviewPage.header')}
                    />
                </Col>
                <Col
                    span={24}
                    style={{ textAlign: 'center' }}
                >
                    <Steps
                        current={currentStepIndex}
                        items={steps.map((step) => {
                            // We don't actually need any properties to make a basic Steps component: Just create an empty object for each step so that we have the correct number.
                            // Optionally we could set labels, icons, etc. here
                            return {};
                        })}
                        responsive={false}
                    />
                </Col>

                <Col
                    span={24}
                >
                    <Row
                        align="middle"
                        justify="center"
                    >
                        <Col
                            span={24}
                        >
                            <header
                                style={{
                                    fontWeight: 'lighter',
                                    fontSize: '30px',
                                    textAlign: 'center',
                                }}
                            >
                                {t('Pages.InterviewPage.section_header', { sectionNumber: currentStepIndex + 1 })} {steps[currentStepIndex]?.title}
                            </header>
                        </Col>

                        <Col
                            span={24}
                        >
                            {steps[currentStepIndex]?.content}
                        </Col>
                    </Row>

                </Col>

                <Col
                    span={24}
                    style={{ textAlign: 'center' }}
                >
                    <Row
                        align="middle"
                        justify="space-between"
                        gutter={20}
                    >
                        <Col
                            xxl={8}
                            xl={10}
                            lg={10}
                            md={10}
                            sm={12}
                            xs={12}
                        >
                            {currentStepIndex > 0 &&
                                <AppButton
                                    size="middle"
                                    onClick={() => handlePrevious()}
                                    style={{ width: '100%' }}
                                >
                                    {t('Common.previous')}
                                </AppButton>
                            }
                        </Col>

                        <Col
                            xxl={8}
                            xl={10}
                            lg={10}
                            md={10}
                            sm={12}
                            xs={12}
                        >
                            {currentStepIndex < steps.length - 1 &&
                                <AppButton
                                    size="middle"
                                    onClick={() => handleNext()}
                                    style={{ width: '100%' }}
                                >
                                    {t('Common.next')}
                                </AppButton>
                            }
                            {currentStepIndex === steps.length - 1 &&
                                <AppButton
                                    size="middle"
                                    type="primary"
                                    onClick={() => handleSubmit()}
                                    style={{ width: '100%' }}
                                >
                                    {t('Common.submit')}
                                </AppButton>
                            }
                        </Col>
                    </Row>
                </Col>
            </Row>
        </PageWrapper>
    );
};

